import { ReactNode } from "react";
import { cn } from "~/lib/utils";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import type { AgentStarterPrompt, PartialAgent } from "~/services/agents";
import { StarterPrompts } from "./StarterPrompts";

interface EmptyConversationViewProps {
  // Agent whose avatar, name, and description anchor the view. Null while the
  // bound agent is still resolving.
  agent: PartialAgent | null;
  // Replaces the default 80px avatar. Defaults to a static AgentAvatar.
  avatar?: ReactNode;
  // Rendered inline to the right of the agent name — used by the New Session
  // splash for the agent-switcher button.
  nameAction?: ReactNode;
  // Rendered beneath the agent name/description, centered — used by the splash
  // for the dataset chip.
  headerExtras?: ReactNode;
  starterPrompts: AgentStarterPrompt[];
  onPromptClick: (text: string) => void;
  // Pin the avatar at a fixed offset from the top instead of vertically
  // centering the whole stack. Used by the New Session splash so swapping
  // agents (which changes the name/description/prompt height below) never
  // shifts the avatar. The live empty conversation has a fixed agent, so it
  // stays centered.
  anchorTop?: boolean;
  // The composer.
  children: ReactNode;
}

// Centered "empty conversation" layout: a large agent avatar, the agent's
// name/description, optional starter prompts, and a composer. Shared by the
// live empty-conversation state and the New Session splash so the two stay
// visually identical.
export const EmptyConversationView = ({
  agent,
  avatar,
  nameAction,
  headerExtras,
  starterPrompts,
  onPromptClick,
  anchorTop = false,
  children,
}: EmptyConversationViewProps) => (
  <div
    className={cn(
      "flex flex-1 flex-col items-center gap-8 overflow-y-auto px-8 pb-8",
      anchorTop ? "justify-start pt-[20vh]" : "justify-center pt-8"
    )}
  >
    {agent && (
      <div className="flex flex-col items-center gap-4 text-center">
        {avatar ?? <AgentAvatar id={agent.id} name={agent.name} size={80} />}
        <div>
          <div className="flex items-center justify-center gap-2">
            <h1 className="text-2xl font-bold text-foreground">{agent.display_name}</h1>
            {nameAction}
          </div>
          {agent.description && (
            <p className="mt-2 max-w-md text-sm text-muted-foreground">{agent.description}</p>
          )}
        </div>
        {headerExtras}
      </div>
    )}
    <div className="w-full max-w-2xl">{children}</div>
    {starterPrompts.length > 0 && (
      <StarterPrompts prompts={starterPrompts} onPromptClick={onPromptClick} />
    )}
  </div>
);
